/** Imported duels keep their frozen gear, but malformed arithmetic never reaches the simulator. */
import type { HeroBuild } from '../shared/equipment';

const stats = ['maxHp','attack','maxStamina','startFervor','guardReduction','damageReduction'] as const;
const effects = ['counterDamage','counterFervor','guardHeal','prayerBonus','firstHitShield'] as const;
const object = (x:unknown):x is Record<string,unknown> => typeof x==='object'&&x!==null&&!Array.isArray(x);
const text = (x:unknown):x is string => typeof x==='string'&&x.length>0&&x.length<=128;
const number = (x:unknown,lo=0,hi=1e6):x is number => typeof x==='number'&&Number.isFinite(x)&&x>=lo&&x<=hi;
function numbers(x:unknown,keys:readonly string[],partial=false) {
  return object(x)&&Object.keys(x).every(k=>keys.includes(k))&&keys.every(k=>partial&&x[k]===undefined||number(x[k],partial?-1e6:0));
}
export function validFrozenBuild(x:unknown):x is HeroBuild {
  if(!object(x)||x.version!==1||!['sword','hammer','staff','daggers'].includes(String(x.weapon)))return false;
  const items=x.items;
  if(!object(items)||!['weapon','armour','boots','charm'].every(k=>text(items[k])))return false;
  if(x.outfit!==null&&!text(x.outfit))return false;
  if(!numbers(x.stats,stats)||!object(x.stats)||!number(x.stats.maxHp,1)||!number(x.stats.maxStamina,1,100)||!number(x.stats.startFervor,0,100))return false;
  if(!numbers(x.effects,effects))return false;
  const ability=x.weapon==='staff'||x.weapon==='hammer'?'starfall':'thousandCuts';
  const technique:Record<string,string>={sword:'solarCleave',hammer:'seismicSlam',staff:'tempest',daggers:'shadowDance'};
  // Existing duels keep their single frozen ability. New duels carry both;
  // validation never injects a new move or recomputes the saved loadout.
  if(!Array.isArray(x.abilities)||x.abilities.length<1||x.abilities.length>2||
    !x.abilities.includes(ability)||new Set(x.abilities).size!==x.abilities.length||
    !x.abilities.every(id=>id===ability||id===technique[String(x.weapon)]))return false;
  if(!Array.isArray(x.synergies)||x.synergies.length>100||!x.synergies.every(text))return false;
  return Array.isArray(x.sources)&&x.sources.length<=100&&x.sources.every(source=>object(source)&&
    ['base','level','training','item','synergy'].includes(String(source.kind))&&text(source.id)&&
    numbers(source.stats,stats,true)&&numbers(source.effects,effects,true));
}

export function validateCombatBuild(x:unknown):string[] {
  if(!object(x)||typeof x.v!=='number'||!Number.isFinite(x.v))return ['combat: expected an object with a numeric v'];
  const active=x.active;
  if(!object(active)||!object(active.state)||!object(active.state.spec)||active.state.spec.build===undefined)return [];
  return validFrozenBuild(active.state.spec.build)?[]:['combat: invalid frozen equipment build'];
}
